import { Injectable } from '@angular/core';
import { Subject } from 'rxjs/Subject';
import { VcaAlertsService } from '../../../common/vca-alerts';
import { HttpUtils } from '../../../common/http/http-utils';
import 'rxjs/add/operator/map';
import { HttpClient } from '@angular/common/http';

export interface GenerateTechSupportJob {
    jobId: string
}

export interface FleetComponentJob {
    jobId: string
}

export interface FleetComponentData {
    state: string,
    response: any
}

export interface AlarmData {
    jobId: string
}

export interface TechSupportData {
    state: string,
    jobId: string,
    fileName: any,
    failedFleetAppliances: any
}

export interface HcmData {
    data: any
}

@Injectable()
export class TroubleshootingService {
    private _alarmData$: Subject<AlarmData[]>;
    private _generateTechSupportJob$: Subject<GenerateTechSupportJob[]>;
    private _fleetComponentJob$: Subject<FleetComponentJob[]>;
    private _fleetComponentData$: Subject<FleetComponentData[]>;
    private _techSupportData$: Subject<TechSupportData[]>;
    private _hcmData$: Subject<HcmData[]>;
    private dataStore: {
        alarmData: AlarmData[],
        fleetComponentJob: FleetComponentJob[],
        generateTechSupportJob: GenerateTechSupportJob[],
        fleetComponentData: FleetComponentData[],
        techSupportData: TechSupportData[],
        hcmData: HcmData[]
    };
 
    private vsphereBaseUrl;

    private _techSupportRunningData: any = {};

    constructor(public http: HttpClient, private alert: VcaAlertsService) {
        const path = (window.location.pathname.match(/(.*)(\/troubleshooting-ui)(\/.*|$)/) || [])[1];
        this.vsphereBaseUrl = !!path ? path + '/hcx-ui/rest' : '';
        this.dataStore = { alarmData: [], fleetComponentJob: [], generateTechSupportJob: [], fleetComponentData: [], techSupportData: [], hcmData: []  };
        this._alarmData$ = <Subject<AlarmData[]>>new Subject();
        this._fleetComponentJob$ = <Subject<FleetComponentJob[]>>new Subject();
        this._generateTechSupportJob$ = <Subject<GenerateTechSupportJob[]>>new Subject();
        this._fleetComponentData$ = <Subject<FleetComponentData[]>>new Subject();
        this._techSupportData$ = <Subject<TechSupportData[]>>new Subject();
        this._hcmData$ = <Subject<HcmData[]>>new Subject();
    }

    get alarmData() {
        return this._alarmData$.asObservable();
    }

    get fleetComponentJob() {
        return this._fleetComponentJob$.asObservable();
    }

    get fleetComponentData() {
        return this._fleetComponentData$.asObservable();
    }

    get generateTechSupportJob() {
        return this._generateTechSupportJob$.asObservable();
    }

    get techSupportData() {
        return this._techSupportData$.asObservable();
    }

    get hcmData() {
        return this._hcmData$.asObservable();
    }

    /**
      * load HCM
     **/
    loadHcmData() {
        let url = HttpUtils.getBaseUrl() + this.vsphereBaseUrl + "/hybridity/api/metainfo/context/hcsp-dashboard";
        this.http.get(url)
            .subscribe((data:any) => {
            // filter out non VC cloud resources
            if (!!data && !!data.data && !!data.data.localEndpoints) {
                data.data.localEndpoints = data.data.localEndpoints.filter((endPoint) => endPoint.resourceType === 'VC');
            }
            // store in data store
            this.dataStore.hcmData = data;
            // update service instances array from data store
            this._hcmData$.next(this.dataStore.hcmData);
        }, error => {
            this.dataStore.hcmData = [];
            this._hcmData$.next(this.dataStore.hcmData);
            console.log('Could not load hcm data instances.');
        });
    }

    /**
      * load all build data from DB by calling REST api and store it in data store
     **/
    loadAlarmData(isVsphereClient?: boolean, vcGuid?: string) {
        let restUrl = "/hybridity/api/techsupport/alerts";
        if ( isVsphereClient && vcGuid ) {
            restUrl = HttpUtils.getBaseUrl() + this.vsphereBaseUrl + restUrl + '?vcGuid=' + vcGuid + '&cb=' + Date.now();
        } else {
            restUrl = `${HttpUtils.getBaseUrl()}${restUrl}`;
        }
        this.http.get(restUrl)
            .subscribe((data:any) => {
            // store in data store
            this.dataStore.alarmData = data;
            // update service instances array from data store
            this._alarmData$.next(this.dataStore.alarmData);
        }, error => {
            this.dataStore.alarmData = [];
            this._alarmData$.next(this.dataStore.alarmData);
            console.log('Could not load alarm data instances.');
        });
    }

        /**
      * load all build data from DB by calling REST api and store it in data store
     **/
    loadFleetComponentJob(isVsphereClient?: boolean, vcGuid?: string) {
        let restUrl = "/hybridity/api/techsupport/components";
        if ( isVsphereClient && vcGuid ) {
            restUrl = HttpUtils.getBaseUrl() + this.vsphereBaseUrl + restUrl + '?vcGuid=' + vcGuid + '&cb=' + Date.now();
        } else {
            restUrl = `${HttpUtils.getBaseUrl()}${restUrl}`;
        }
        this.http.get(restUrl)
            .subscribe((data:any) => {
            // store in data store
            this.dataStore.fleetComponentJob = data;
            // update service instances array from data store
            this._fleetComponentJob$.next(this.dataStore.fleetComponentJob);
        }, error => {
            this.dataStore.fleetComponentJob = [];
            this._fleetComponentJob$.next(this.dataStore.fleetComponentJob);
            console.log('Could not load fleetComponentJob instances.');
        });
    }

        /**
      * load all build data from DB by calling REST api and store it in data store
     **/
    loadFleetComponentData(jobId: string, isVsphereClient?: boolean, vcGuid?: string) {
        let restUrl = `/hybridity/api/techsupport/task/${jobId}`;
        if ( isVsphereClient && vcGuid ) {
            restUrl = HttpUtils.getBaseUrl() + this.vsphereBaseUrl + restUrl + '?vcGuid=' + vcGuid + '&cb=' + Date.now();
        } else {
            restUrl = `${HttpUtils.getBaseUrl()}${restUrl}`;
        }
        this.http.get(restUrl)
            .subscribe((data:any) => {
            // store in data store
            this.dataStore.fleetComponentData = data;
            // update service instances array from data store
            this._fleetComponentData$.next(this.dataStore.fleetComponentData);
        }, error => {
            this.dataStore.fleetComponentData = [];
            this._fleetComponentData$.next(this.dataStore.fleetComponentData);
            console.log('Could not load fleetComponentData instances.');
        });
    }

    /*
        To Post for Collection
    */
    requestLogBundle(payload: any, isVsphereClient?: boolean, vcGuid?: string) {
        let restUrl = "/hybridity/api/techsupport?action=collectTechSupportBundle";
        if ( isVsphereClient && vcGuid ) {
            restUrl = HttpUtils.getBaseUrl() + this.vsphereBaseUrl + restUrl + '&vcGuid=' + vcGuid + '&cb=' + Date.now();
        } else {
            restUrl = `${HttpUtils.getBaseUrl()}${restUrl}`;
        }
        return this.http
            .post(restUrl, JSON.stringify(payload))
            .subscribe((data:any) => {
                // store in data store
                this.dataStore.generateTechSupportJob = data;
                // update service instances array from data store
                this._generateTechSupportJob$.next(this.dataStore.generateTechSupportJob);
            }, error => {
                this.dataStore.generateTechSupportJob = [];
                this._generateTechSupportJob$.next(this.dataStore.generateTechSupportJob);
                console.log('Could not load generate TechSupport Job instances.');
            }
            );
    }

    /**
      * load all build data from DB by calling REST api and store it in data store
     **/
    loadTechSupportData(jobId: string, isVsphereClient?: boolean, vcGuid?: string) {
        let restUrl = `/hybridity/api/techsupport/task/${jobId}`;
        if ( isVsphereClient && vcGuid ) {
            restUrl = HttpUtils.getBaseUrl() + this.vsphereBaseUrl + restUrl + '?vcGuid=' + vcGuid + '&cb=' + Date.now();
        } else {
            restUrl = `${HttpUtils.getBaseUrl()}${restUrl}`;
        }
        this.http.get(restUrl)
            .subscribe((data:any) => {
            // store in data store
            this.dataStore.techSupportData = data;
            // update service instances array from data store
            this._techSupportData$.next(this.dataStore.techSupportData);
        }, error => {
            this.dataStore.fleetComponentData = [];
            this._techSupportData$.next(this.dataStore.techSupportData);
            console.log('Could not load techSupportData instances.');
        });
    }


    acknowledgeAlarm(payload: any, isVsphereClient?: boolean, vcGuid?: string) {
        let restUrl = "/hybridity/api/techsupport/alerts?action=acknowledge";
        if ( isVsphereClient && vcGuid ) {
            restUrl = HttpUtils.getBaseUrl() + this.vsphereBaseUrl + restUrl + '&vcGuid=' + vcGuid + '&cb=' + Date.now();
        } else {
            restUrl = `${HttpUtils.getBaseUrl()}${restUrl}`;
        }
        return this.http
            .post(restUrl, JSON.stringify(payload));
    }

    getTechsupportSettings(isVsphereClient?: boolean, vcGuid?: string) {
        let url = "/hybridity/api/techsupport/settings";
        if ( isVsphereClient && vcGuid ) {
            url = HttpUtils.getBaseUrl() + this.vsphereBaseUrl + url + '?vcGuid=' + vcGuid + '&cb=' + Date.now();
        } else {
            url = `${HttpUtils.getBaseUrl()}${url}`;
        }
        return this.http.get(url);
    }

    updateTechsupportSettings(body: any, isVsphereClient?: boolean, vcGuid?: string) {
        let url = "/hybridity/api/techsupport/settings";
        if ( isVsphereClient && vcGuid ) {
            url = HttpUtils.getBaseUrl() + this.vsphereBaseUrl + url + '?vcGuid=' + vcGuid + '&cb=' + Date.now();
        } else {
            url = `${HttpUtils.getBaseUrl()}${url}`;
        }
        return this.http.put(url, body);
    }

    setTechSupportRunningState(data: any) {
        this._techSupportRunningData = data;
    }

    getTechSupportRunningState() {
        return this._techSupportRunningData;
    }

}


// WEBPACK FOOTER //
// ./src/troubleshooting/src/troubleshooting-ui/troubleshooting.service.ts